import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { filterBy } from '@progress/kendo-data-query';
import {
    ComboBoxHandle,
    DropDownList,
    DropDownListCloseEvent,
    DropDownListFilterChangeEvent,
    DropDownListProps,
} from '@progress/kendo-react-dropdowns';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import DarkTooltip from '@components/core/dark-tool-tip/dark-tool-tip';
import MultiSelectNoResults from '@components/core/multi-select/multi-select-no-results';
import { DashboardSite } from '@pages/central-monitoring/legacy-dashboards';

interface SiteDropdownListItemProps {
    element: React.ReactElement<HTMLLIElement | HTMLSpanElement>;
    site: DashboardSite;
}

const SiteDropdownListItem = ({ element, site }: SiteDropdownListItemProps) => {
    const children = (
        <span className="k-display-flex k-w-full k-align-items-center k-justify-content-between">
            {element.props.children as ReactNode}
            <span>
                {site.default && <FontAwesomeIcon icon={['far', 'asterisk']} className="default-site-asterisk" />}
                {site.favourite && <FontAwesomeIcon icon={['fas', 'star']} className="favourite-site-star" />}
            </span>
        </span>
    );

    return React.cloneElement(element, element.props, children);
};

interface SiteDropdownProps extends Omit<DropDownListProps, 'onFilterChange'> {
    data: DashboardSite[];
    value: DashboardSite | null;
}

export const SiteDropdown = ({ data, value, onChange }: SiteDropdownProps) => {
    const [datasource, setDatasource] = useState<DashboardSite[]>([]);
    const [filter, setFilter] = useState<string>('');
    const ref = useRef<ComboBoxHandle>();

    useEffect(() => {
        ref.current?.element?.setAttribute('data-testid', 'site-dropdown');
    }, []);

    const resetDatasource = () => {
        setDatasource(data ? [...data] : []);
    };

    useEffect(() => {
        resetDatasource();
    }, [data]);

    const onFilterChange = (event: DropDownListFilterChangeEvent) => {
        setDatasource(filterBy([...data], event.filter));
        setFilter(event.filter.value as string);
    };

    const onCloseHandler = (event: DropDownListCloseEvent) => {
        setFilter('');
        resetDatasource();
    };

    return (
        <div className="site-dropdown-wrapper">
            <DarkTooltip title="Site" arrow placement="left">
                <FontAwesomeIcon className="icon k-h-6" icon={['fal', 'hospital']} />
            </DarkTooltip>
            <DropDownList
                ref={ref}
                className="select-with-categories"
                onChange={onChange}
                dataItemKey="id"
                textField="name"
                value={value}
                data={datasource}
                filterable
                filter={filter}
                onFilterChange={onFilterChange}
                onClose={onCloseHandler}
                style={{ height: '28px' }}
                itemRender={(li, itemProps) => (
                    <SiteDropdownListItem element={li} site={itemProps.dataItem as DashboardSite} />
                )}
                listNoDataRender={() => <MultiSelectNoResults />}
            />
        </div>
    );
};
